import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {Project} from "@shared/interfaces/project.interface";
import { useAuth } from '@/contexts/AuthContext';

interface FetchGuardOptions {
  isAuthenticated: boolean;
  isPublicRoute: boolean;
  path: string;
}

export function shouldFetchProjects({
  isAuthenticated,
  isPublicRoute,
  path,
}: FetchGuardOptions): boolean {
  if (!isAuthenticated || isPublicRoute) {
    return false;
  }

  // The project selection screen already issues its own paginated request.
  // Avoid duplicating that call from the context provider when the user is on
  // the root path so we do not trigger two API requests on initial load.
  if (path === '/') {
    return false;
  }

  return true;
}



interface ProjectContextType {
  projectId: string | null;
  project: Project | null;
  projects: Project[] | undefined;
  isLoading: boolean;
  setProjectId: (id: string | null) => void;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export function ProjectProvider({ children }: { children: ReactNode }) {
  // Initialize immediately from localStorage
  const [projectId, setProjectIdState] = useState<string | null>(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('currentProjectId') || null;
    }
    return null;
  });

  // Only fetch projects when authenticated and not on public routes
  const { isAuthenticated } = useAuth();
  const path = typeof window !== 'undefined' ? window.location.pathname : '/';
  const isPublicRoute = path === '/login'
    || path.startsWith('/share/')
    || path.startsWith('/public/')
    || path.startsWith('/value-assessment/public-input')
    || path.startsWith('/value-assessment/input');

  const shouldRequestProjects = shouldFetchProjects({
    isAuthenticated,
    isPublicRoute,
    path,
  });

  const { data: projectsResponse, isLoading } = useQuery<{ data: Project[] }>({
    queryKey: ['/api/projects'],
    enabled: shouldRequestProjects,
    staleTime: 5 * 60 * 1000, // 5 minutes cache
    refetchOnWindowFocus: false,
  });

  const projects = projectsResponse?.data;

  // Auto-select first project if none is selected
  useEffect(() => {
    if (projects && projects.length > 0 && !projectId) {
      const firstProject = projects[0];
      setProjectIdState(firstProject.id);
      localStorage.setItem('currentProjectId', firstProject.id);
    }
  }, [projects, projectId]);

  // Listen for URL changes
  useEffect(() => {
    const handleURLChange = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const urlProjectId = urlParams.get('project');
      
      if (urlProjectId && urlProjectId !== projectId) {
        setProjectIdState(urlProjectId);
        localStorage.setItem('currentProjectId', urlProjectId);
      }
    };

    // Check immediately
    handleURLChange();
    
    // Listen for popstate events (back/forward navigation)
    window.addEventListener('popstate', handleURLChange);
    
    return () => window.removeEventListener('popstate', handleURLChange);
  }, [projectId]);

  const setProjectId = (id: string | null) => {
    setProjectIdState(id);
    if (id) {
      localStorage.setItem('currentProjectId', id);
    } else {
      localStorage.removeItem('currentProjectId');
    }
  };

  const project = projects?.find(p => p.id === projectId) || null;

  const value: ProjectContextType = {
    projectId,
    project,
    projects,
    isLoading,
    setProjectId,
  };

  return (
    <ProjectContext.Provider value={value}>
      {children}
    </ProjectContext.Provider>
  );
}

export function useProjectContext() {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error('useProjectContext must be used within a ProjectProvider');
  }
  return context;
}